import { apiClient } from './client';
import type { SuccessEnvelope } from './auth';
import type { paths } from './generated/schema';
import { unwrapData } from './utils';

export type UserProfile = Record<string, unknown>;

type PreferencesResponse =
  paths['/users/preferences']['get']['responses']['200']['content']['application/json'];

type PreferencesData = PreferencesResponse extends { data?: infer D } ? NonNullable<D> : { preferences: Record<string, never> };

type PreferencesPayload = PreferencesData extends { preferences: infer P } ? P : Record<string, never>;

export type UserNotificationPreferences = PreferencesPayload;

type JsonBody<Path> = Path extends { requestBody?: { content: { 'application/json': infer B } } }
  ? B
  : Record<string, never>;

export type UpdateUserPreferencesInput = JsonBody<paths['/users/preferences']['put']>;

type UpdateUserProfileBody = JsonBody<paths['/users/profile']['put']>;
export type UpdateUserProfilePayload = UpdateUserProfileBody & { email?: string };

type ConsentStatusResponse =
  paths['/users/consent-status']['get']['responses']['200']['content']['application/json'];

export type ConsentStatus = ConsentStatusResponse extends { data?: infer D } ? NonNullable<D> : Record<string, never>;

type UpdateConsentInput = JsonBody<paths['/users/consent']['post']>;

type UpdateConsentResponse =
  paths['/users/consent']['post']['responses']['200']['content']['application/json'];
type UpdateConsentData = UpdateConsentResponse extends { data?: infer D } ? NonNullable<D> : Record<string, never>;

type SessionsResponse =
  paths['/users/sessions']['get']['responses']['200']['content']['application/json'];
type SessionsData = SessionsResponse extends { data?: infer D } ? NonNullable<D> : Record<string, never>;
type SessionItem = SessionsData extends { sessions?: ReadonlyArray<infer S> } ? S : Record<string, never>;
export type UserSession = SessionItem;
export type UserSessionsPayload = {
  sessions: UserSession[];
  count: number;
};

type LogoutAllSessionsResponse =
  paths['/users/sessions']['delete']['responses']['200']['content']['application/json'];

type RequestDataExportInput = JsonBody<paths['/users/exports']['post']>;

type RequestDataExportQueued =
  paths['/users/exports']['post']['responses']['202']['content']['application/json'];

type RequestDataExportExisting =
  paths['/users/exports']['post']['responses']['200']['content']['application/json'];

type RequestDataExportData = (RequestDataExportQueued extends { data?: infer D }
  ? NonNullable<D>
  : never) | (RequestDataExportExisting extends { data?: infer D }
  ? NonNullable<D>
  : never);

type PurgeUserDataInput = JsonBody<paths['/users/me/data/purge']['post']>;

type PurgeUserDataSuccess =
  paths['/users/me/data/purge']['post']['responses']['200']['content']['application/json'];

export type PurgeUserDataResponse = PurgeUserDataSuccess extends { data?: infer D }
  ? NonNullable<D>
  : Record<string, never>;

type DeleteUserAccountInput = JsonBody<paths['/users/me']['delete']>;

type DeleteUserAccountSuccess =
  paths['/users/me']['delete']['responses']['200']['content']['application/json'];

export type DeleteUserAccountResponse = DeleteUserAccountSuccess extends { data?: infer D }
  ? NonNullable<D>
  : Record<string, never>;

export const getUserProfile = async (): Promise<UserProfile> => {
  const result = await apiClient.GET('/users/profile');

  const payload = result as {
    data?: SuccessEnvelope<{ user: UserProfile }>;
    error?: Error;
    response?: Response;
  };

  if (payload.error) {
    throw payload.error;
  }

  const envelope = payload.data;
  if (!envelope || !envelope.success) {
    throw new Error('Failed to load profile');
  }

  return envelope.data.user;
};

export const updateUserProfile = async (
  input: UpdateUserProfilePayload
): Promise<UserProfile> => {
  const result = await apiClient.PUT('/users/profile', { body: input });

  const payload = result as {
    data?: SuccessEnvelope<{ user: UserProfile }>;
    error?: Error;
    response?: Response;
  };

  if (payload.error) {
    throw payload.error;
  }

  const envelope = payload.data as SuccessEnvelope<{ user: UserProfile }> | undefined;
  if (!envelope || !envelope.success) {
    throw new Error('Failed to update profile');
  }

  return envelope.data.user;
};

export const getUserPreferences = async (): Promise<UserNotificationPreferences> => {
  const result = await apiClient.GET('/users/preferences');
  const data = unwrapData<PreferencesData>(result);

  return (data.preferences as PreferencesPayload) ?? ({} as PreferencesPayload);
};

export const updateUserPreferences = async (
  input: UpdateUserPreferencesInput
): Promise<UserNotificationPreferences> => {
  const result = await apiClient.PUT('/users/preferences', {
    body: input,
  });

  const data = unwrapData<PreferencesData>(result);
  return (data.preferences as PreferencesPayload) ?? ({} as PreferencesPayload);
};

export const getConsentStatus = async (): Promise<ConsentStatus> => {
  const result = await apiClient.GET('/users/consent-status');
  return unwrapData<ConsentStatus>(result);
};

export const updateConsent = async (
  input: UpdateConsentInput
): Promise<UpdateConsentData> => {
  const result = await apiClient.POST('/users/consent', {
    body: input,
  });

  return unwrapData<UpdateConsentData>(result);
};

export const getUserSessions = async (): Promise<UserSessionsPayload> => {
  const result = await apiClient.GET('/users/sessions');
  const data = unwrapData<SessionsData>(result);
  return {
    sessions: (data.sessions as SessionItem[]) ?? [],
    count: (data as { count?: number }).count ?? ((data.sessions as SessionItem[])?.length ?? 0),
  };
};

export const deleteUserSession = async (sessionId: string): Promise<void> => {
  const result = await apiClient.DELETE('/users/sessions/{sessionId}', {
    params: { path: { sessionId } },
  });

  const payload = result as { error?: Error };
  if (payload.error) {
    throw payload.error;
  }
};

export const logoutAllSessions = async (): Promise<LogoutAllSessionsResponse> => {
  const result = await apiClient.DELETE('/users/sessions');
  return unwrapData<LogoutAllSessionsResponse>(result);
};

export const requestDataExport = async (
  input?: RequestDataExportInput
): Promise<RequestDataExportData> => {
  const result = await apiClient.POST('/users/exports', input ? { body: input } : {});
  return unwrapData<RequestDataExportData>(result);
};

export const purgeUserData = async (
  input: PurgeUserDataInput = { confirm: true }
): Promise<PurgeUserDataResponse> => {
  const result = await apiClient.POST('/users/me/data/purge', {
    body: input,
  });
  return unwrapData<PurgeUserDataResponse>(result);
};

export const deleteUserAccount = async (
  input: DeleteUserAccountInput
): Promise<DeleteUserAccountResponse> => {
  const result = await apiClient.DELETE('/users/me', {
    body: input,
  });
  return unwrapData<DeleteUserAccountResponse>(result);
};

export const restoreContactEmail = async (): Promise<UserProfile> => {
  const result = await apiClient.POST('/users/contact-email/restore', {});

  const payload = result as {
    data?: SuccessEnvelope<{ user: UserProfile }>;
    error?: Error;
    response?: Response;
  };

  if (payload.error) {
    throw payload.error;
  }

  const envelope = payload.data;
  if (!envelope || !envelope.success) {
    throw new Error('Failed to restore contact email');
  }

  return envelope.data.user;
};
